import { EmeraldCheckbox } from "@emerald-react/checkbox";
import { EmeraldIconButton } from "@emerald-react/icon-button";
import { EmeraldSideSheet } from "@emerald-react/side-sheet";
import { EmeraldSidesheetPositionType } from "@emerald/nxcore";
import {
  useCurrentPropertyId,
  useIsPropertiesDrawerOpen,
} from "@hooks/useSurveySelectors";
import useSurveyState from "@hooks/useSurveyState";
import {
  surveyCoreApi,
  useUpdateSurveyFlagsMutation,
} from "@services/survey-core";
import Analytics from "@shared/utils/tagManagerAnalytics";
import PropertiesListDrawer from "@survey-properties/PropertiesListDrawer";
import PropertyDetailForm from "@survey-properties/PropertyDetailForm";
import {
  DetailedSurvey,
  type PropertySortOrder,
} from "@survey-types/survey.core.types";
import {
  currentPropertyId as currentPropertyIdAction,
  setOpenPropertiesDrawer,
} from "@survey/surveySlice";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import EditColumns from "../comparisonSection/EditColumns";

// Type for property flags that can be toggled
type PropertyFlag = Pick<
  DetailedSurvey,
  | "showCurrentLocationPD"
  | "isIncludePhotos"
  | "isIncludeSitePlans"
  | "isIncludeFlyers"
>;
type PropertyFlagKey = keyof PropertyFlag;

type LabelDropdown = "sitePlans" | "flyers" | null;

const DIRECTIONAL_SORTS: PropertySortOrder[] = [
  "NORTH_TO_SOUTH",
  "SOUTH_TO_NORTH",
  "EAST_TO_WEST",
  "WEST_TO_EAST",
];

const PropertiesSection = () => {
  const { survey, dispatch } = useSurveyState();
  const [isEditColumnsOpen, setIsEditColumnsOpen] = useState(false);
  // const [isPropertiesDrawerOpen, setIsPropertiesDrawerOpen] = useState(false);
  const currentPropertyId = useCurrentPropertyId();
  const isPropertiesDrawerOpen = useIsPropertiesDrawerOpen();
  const [updateSurveyFlags] = useUpdateSurveyFlagsMutation();

  // Label rename dropdown state
  const [activeLabelDropdown, setActiveLabelDropdown] =
    useState<LabelDropdown>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  // Wrapper refs for the pencil buttons (position source) and portal dropdown (click-outside)
  const sitePlanButtonRef = useRef<HTMLSpanElement>(null);
  const flyerButtonRef = useRef<HTMLSpanElement>(null);
  const dropdownPortalRef = useRef<HTMLDivElement>(null);

  // When the user closes a property and returns to the list, refetch the survey
  // if a directional sort is active — coordinates may have changed and the BE
  // will return properties in the recalculated order.
  const prevPropertyIdRef = useRef(currentPropertyId);
  useEffect(() => {
    const wasEditing = prevPropertyIdRef.current !== null;
    prevPropertyIdRef.current = currentPropertyId;

    if (
      wasEditing &&
      currentPropertyId === null &&
      survey?.id &&
      DIRECTIONAL_SORTS.includes(survey.propertySortOrder as PropertySortOrder)
    ) {
      dispatch(
        surveyCoreApi.util.invalidateTags([{ type: "Survey", id: survey.id }]),
      );
    }
  }, [currentPropertyId, dispatch, survey?.id, survey?.propertySortOrder]);

  const toggleOpenEditColumns = (e?: React.MouseEvent) => {
    e?.preventDefault();
    setIsEditColumnsOpen(!isEditColumnsOpen);
  };

  const handleOpenPropertiesDrawer = () => {
    // Send analytics event when property drawer opens
    if (survey?.id !== undefined) {
      Analytics.send({
        event: "userDataReady",
        user_id: localStorage.getItem("employeeId"),
        jobTitle: localStorage.getItem("position"),
        officeName: localStorage.getItem("office"),
        email: localStorage.getItem("email"),
        functUnitName: localStorage.getItem("functUnitName"),
        SiteIQUserID: survey?.createdBy || null, // Use createdBy as fallback since user property doesn't exist
        teamName: survey?.teamName, // team property not available on DetailedSurvey type
      });
    }
    dispatch(setOpenPropertiesDrawer(true));
    dispatch(currentPropertyIdAction(null));
  };

  const handleClosePropertiesDrawer = () => {
    dispatch(setOpenPropertiesDrawer(false));
    dispatch(currentPropertyIdAction(null));
    if (survey?.id) {
      dispatch(
        surveyCoreApi.util.invalidateTags([{ type: "Survey", id: survey.id }]),
      );
    }
  };

  const handlePropertyFlagChange = (flag: PropertyFlagKey, value: boolean) => {
    updateSurveyFlags({ id: survey?.id, [flag]: value });
  };

  const handleLabelChange = (
    field: "sitePlanLabel" | "flyerLabel",
    value: string,
  ) => {
    updateSurveyFlags({ id: survey?.id, [field]: value });
    setActiveLabelDropdown(null);
    setDropdownPosition(null);
  };

  const openLabelDropdown = (type: "sitePlans" | "flyers") => {
    const buttonRef = type === "sitePlans" ? sitePlanButtonRef : flyerButtonRef;
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setDropdownPosition({ top: rect.bottom + 4, left: rect.left });
    setActiveLabelDropdown(type);
  };

  // Close label dropdown when clicking outside (portal-aware)
  useEffect(() => {
    if (!activeLabelDropdown) return;
    const buttonRef =
      activeLabelDropdown === "sitePlans" ? sitePlanButtonRef : flyerButtonRef;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownPortalRef.current &&
        !dropdownPortalRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setActiveLabelDropdown(null);
        setDropdownPosition(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeLabelDropdown]);
  return survey ? (
    <section className="flex flex-col gap-y-2 w-full text-sm">
      <div>
        You can change the order or visibility of property data by{" "}
        <a
          href="#"
          className="text-primary underline"
          onClick={toggleOpenEditColumns}
        >
          editing the columns
        </a>{" "}
        and the order properties are shown by{" "}
        <a
          href="#"
          className="text-primary underline"
          onClick={handleOpenPropertiesDrawer}
        >
          managing properties
        </a>
        .
      </div>
      <div>
        <EmeraldCheckbox
          label="Show Current Location"
          checked={survey.showCurrentLocationPD}
          disabled={
            survey.properties.filter((property) => property.isCurrentLocation)
              .length === 0
          }
          onChange={(e) => handlePropertyFlagChange("showCurrentLocationPD", e)}
        />
        <EmeraldCheckbox
          label="Show Extra Photos"
          checked={survey.isIncludePhotos}
          onChange={(e) => handlePropertyFlagChange("isIncludePhotos", e)}
        />
        <EmeraldCheckbox
          label="Show Floorplan"
          checked={survey.isIncludeSitePlans}
          onChange={(e) => handlePropertyFlagChange("isIncludeSitePlans", e)}
        />
        <EmeraldCheckbox
          label="Show Property Flyer"
          checked={survey.isIncludeFlyers}
          onChange={(e) => handlePropertyFlagChange("isIncludeFlyers", e)}
        />
      </div>

      {/* Attachment label rename controls */}
      <div className="pt-1">
        <p className="text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">
          Choose Attachment Labels:
        </p>
        {/* Site Plans label row */}
        <div className="flex items-center gap-1 py-0.5">
          <span className="text-sm">
            {survey.sitePlanLabel || "Site Plans"}
          </span>
          <span ref={sitePlanButtonRef} className="inline-flex">
            <EmeraldIconButton
              icon="edit"
              type="Icon"
              onClick={(e) => {
                e.stopPropagation();
                activeLabelDropdown === "sitePlans"
                  ? (setActiveLabelDropdown(null), setDropdownPosition(null))
                  : openLabelDropdown("sitePlans");
              }}
              aria-label="Change Site Plans label"
            />
          </span>
        </div>
        {/* Flyers label row */}
        <div className="flex items-center gap-1 py-0.5">
          <span className="text-sm">{survey.flyerLabel || "Flyers"}</span>
          <span ref={flyerButtonRef} className="inline-flex">
            <EmeraldIconButton
              icon="edit"
              type="Icon"
              onClick={(e) => {
                e.stopPropagation();
                activeLabelDropdown === "flyers"
                  ? (setActiveLabelDropdown(null), setDropdownPosition(null))
                  : openLabelDropdown("flyers");
              }}
              aria-label="Change Flyers label"
            />
          </span>
        </div>
      </div>

      {/* Portal dropdown — rendered at document.body to escape accordion stacking contexts */}
      {activeLabelDropdown &&
        dropdownPosition &&
        createPortal(
          <div
            ref={dropdownPortalRef}
            style={{
              position: "fixed",
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              zIndex: 9999,
            }}
            className="min-w-[148px] rounded border border-gray-200 bg-white shadow-lg"
          >
            {(activeLabelDropdown === "sitePlans"
              ? (["Site Plans", "Floor Plans"] as const)
              : (["Flyers", "Brochures"] as const)
            ).map((option) => {
              const currentValue =
                activeLabelDropdown === "sitePlans"
                  ? survey.sitePlanLabel || "Site Plans"
                  : survey.flyerLabel || "Flyers";
              const field =
                activeLabelDropdown === "sitePlans"
                  ? "sitePlanLabel"
                  : "flyerLabel";
              return (
                <button
                  key={option}
                  className={`block w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${
                    currentValue === option
                      ? "font-semibold text-green-700"
                      : ""
                  }`}
                  onClick={() => handleLabelChange(field, option)}
                >
                  {option}
                </button>
              );
            })}
          </div>,
          document.body,
        )}

      <EditColumns isOpen={isEditColumnsOpen} onClose={toggleOpenEditColumns} />

      <EmeraldSideSheet
        opened={isPropertiesDrawerOpen}
        title={`${currentPropertyId ? "" : "Manage Properties"}`}
        position={"right" as EmeraldSidesheetPositionType}
        closeOnOutsideClick={true}
        hasCloseButton={!currentPropertyId && true}
        closeOnEscape={true}
        onClose={handleClosePropertiesDrawer}
        className="w-[75vw]! property-drawer-list"
      >
        {currentPropertyId ? <PropertyDetailForm /> : <PropertiesListDrawer />}
      </EmeraldSideSheet>
    </section>
  ) : null;
};

export default PropertiesSection;
